import { useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Check } from 'lucide-react';

// Multi-select job-role picker with an inline "create new role" field. Roles are the shared
// scheduling Positions, so a role created here is instantly usable in the scheduler too.
export function JobRolesField({ allRoles, setAllRoles, value, onChange }) {
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    if (allRoles.some((r) => r.name.toLowerCase() === name.toLowerCase())) {
      const existing = allRoles.find((r) => r.name.toLowerCase() === name.toLowerCase());
      if (!value.includes(existing.id)) onChange([...value, existing.id]);
      setNewName('');
      return;
    }
    setBusy(true);
    try {
      const { data } = await staffApi.post('/scheduling/positions', { name });
      setAllRoles((r) => [...r, data.position].sort((a, b) => a.name.localeCompare(b.name)));
      onChange([...value, data.position.id]);
      setNewName('');
      toast.success(`Role "${name}" created`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="job-roles-field">
      <Label>Job Roles</Label>
      <div className="flex flex-wrap gap-1.5">
        {allRoles.length === 0 && <span className="text-xs text-muted-ink">No roles yet — create one below.</span>}
        {allRoles.map((r) => {
          const on = value.includes(r.id);
          return (
            <button
              type="button"
              key={r.id}
              data-testid={`job-role-chip-${r.id}`}
              onClick={() => toggle(r.id)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${on ? 'bg-saffron text-white border-saffron' : 'bg-white text-charcoal border-border/60 hover:border-saffron/60'}`}
            >
              {on && <Check size={12} />}
              {r.name}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <Input
          data-testid="job-role-new-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); create(); } }}
          placeholder="Create a new role (e.g. Cook, Cashier)"
          className="h-9"
        />
        <Button type="button" size="sm" variant="outline" data-testid="job-role-add-btn" disabled={busy || !newName.trim()} onClick={create} className="gap-1 shrink-0">
          <Plus size={14} /> Add
        </Button>
      </div>
    </div>
  );
}
